import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, Package, Mail, LogIn, LogOut, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'admin_token';

// Security note: sessionStorage clears on tab close, reducing XSS persistence window
// compared to localStorage. For higher security, the backend should issue httpOnly cookies.
const tokenStore = {
  get: () => sessionStorage.getItem(TOKEN_KEY),
  set: (token) => sessionStorage.setItem(TOKEN_KEY, token),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
};

// Decode JWT payload and check expiry without verifying signature (signature verified server-side)
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    const token = tokenStore.get();
    if (isTokenValid(token)) {
      setIsAuthenticated(true);
      fetchData();
    } else if (token) {
      tokenStore.clear();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      tokenStore.set(response.data.token);
      setIsAuthenticated(true);
      toast.success('Login successful!');
      fetchData();
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    }
  };

  const handleLogout = () => {
    tokenStore.clear();
    setIsAuthenticated(false);
    setOrders([]);
    setContacts([]);
    toast.success('Logged out successfully');
  };

  const fetchData = async () => {
    setLoading(true);
    const token = tokenStore.get();
    if (!isTokenValid(token)) {
      handleLogout();
      toast.error('Session expired. Please login again.');
      setLoading(false);
      return;
    }
    const config = {
      headers: { Authorization: `Bearer ${token}` }
    };

    try {
      const [ordersRes, contactsRes] = await Promise.all([
        axios.get(`${API}/orders`, config),
        axios.get(`${API}/contact`, config)
      ]);
      setOrders(ordersRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    const token = tokenStore.get();
    try {
      await axios.patch(
        `${API}/orders/${orderId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update status');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 border border-[var(--border-soft)]">
          <div className="text-center mb-8">
            <LogIn className="mx-auto text-[var(--accent-gold)] mb-4" size={48} />
            <h1 className="text-3xl mb-2">Admin Login</h1>
            <p className="text-[var(--text-muted)]">Access order management dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Username</label>
              <input
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                data-testid="admin-username-input"
                className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                data-testid="admin-password-input"
                className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
              />
            </div>
            
            <button
              type="submit"
              data-testid="admin-login-button"
              className="w-full px-8 py-4 bg-[var(--accent-gold)] text-white rounded-full text-lg font-medium button-hover"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-16 px-6 lg:px-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl">Admin Dashboard</h1>
          <div className="flex gap-4">
            <button
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-button"
              className="px-6 py-3 bg-white border-2 border-[var(--border-soft)] rounded-full font-medium button-hover flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="px-6 py-3 bg-[var(--primary)] rounded-full font-medium button-hover flex items-center gap-2"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[var(--border-soft)]">
          <button
            onClick={() => setActiveTab('orders')}
            data-testid="tab-orders"
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'orders'
                ? 'text-[var(--accent-gold)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
            }`}
          >
            <Package className="inline mr-2" size={18} />
            Orders ({orders.length})
            {activeTab === 'orders' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent-gold)]"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            data-testid="tab-contacts"
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'contacts'
                ? 'text-[var(--accent-gold)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
            }`}
          >
            <Mail className="inline mr-2" size={18} />
            Contacts ({contacts.length})
            {activeTab === 'contacts' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent-gold)]"></div>
            )}
          </button>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4" data-testid="orders-list">
            {orders.length === 0 ? (
              <div className="text-center py-16 text-[var(--text-muted)]">
                <Package size={48} className="mx-auto mb-4 opacity-50" />
                <p>No orders yet</p>
              </div>
            ) : (
              orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-3xl p-6 border border-[var(--border-soft)] card-hover"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold mb-1">{order.customer_name}</h3>
                      <p className="text-sm text-[var(--text-muted)]">
                        {new Date(order.created_at).toLocaleDateString()} at{' '}
                        {new Date(order.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        data-testid={`order-status-${order.id}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium border-2 ${
                          order.status === 'pending'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                            : order.status === 'confirmed'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : order.status === 'completed'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-700 border-red-200'
                        }`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button
                        onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}
                        data-testid={`view-order-${order.id}`}
                        className="p-2 rounded-full hover:bg-[var(--primary)]/20 transition-colors"
                      >
                        <Eye size={20} />
                      </button>
                    </div>
                  </div>

                  {selectedOrder?.id === order.id && (
                    <div className="mt-4 pt-4 border-t border-[var(--border-soft)] space-y-3 text-sm" data-testid={`order-details-${order.id}`}>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="font-medium">Email:</span> {order.customer_email}
                        </div>
                        <div>
                          <span className="font-medium">Phone:</span> {order.customer_phone}
                        </div>
                        <div>
                          <span className="font-medium">Pickup:</span> {order.pickup_date} at {order.pickup_time}
                        </div>
                        <div>
                          <span className="font-medium">Total:</span> <span className="text-[var(--accent-gold)] font-bold">${order.total_price.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      <div className="bg-[var(--background-secondary)] p-4 rounded-2xl">
                        <h4 className="font-semibold mb-2">Order Items:</h4>
                        {order.items.map((item, idx) => (
                          <div key={idx} className="mb-2 pb-2 border-b border-[var(--border-soft)] last:border-0">
                            <div className="font-medium capitalize">{item.category} - {item.flavor}</div>
                            {item.filling && <div className="text-xs text-[var(--text-muted)]">Filling: {item.filling}</div>}
                            {item.icing && <div className="text-xs text-[var(--text-muted)]">Icing: {item.icing}</div>}
                            <div className="text-xs text-[var(--text-muted)]">Quantity: {item.quantity}</div>
                            {item.size && <div className="text-xs text-[var(--text-muted)]">Size: {item.size}</div>}
                            {item.decoration && <div className="text-xs text-[var(--text-muted)]">Decoration: {item.decoration}</div>}
                            <div className="text-xs font-semibold mt-1">${item.price.toFixed(2)}</div>
                          </div>
                        ))}
                      </div>

                      {order.color_theme && (
                        <div><span className="font-medium">Color Theme:</span> {order.color_theme}</div>
                      )}
                      {order.customization && (
                        <div><span className="font-medium">Customization:</span> {order.customization}</div>
                      )}
                      {order.allergy_notes && (
                        <div><span className="font-medium">Allergy Notes:</span> {order.allergy_notes}</div>
                      )}
                      {order.event_type && (
                        <div><span className="font-medium">Event Type:</span> {order.event_type}</div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4" data-testid="contacts-list">
            {contacts.length === 0 ? (
              <div className="text-center py-16 text-[var(--text-muted)]">
                <Mail size={48} className="mx-auto mb-4 opacity-50" />
                <p>No contact submissions yet</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="bg-white rounded-3xl p-6 border border-[var(--border-soft)]"
                  data-testid={`contact-${contact.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold mb-1">{contact.name}</h3>
                      <p className="text-sm text-[var(--text-muted)]">
                        {new Date(contact.created_at).toLocaleDateString()} at{' '}
                        {new Date(contact.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Email:</span> {contact.email}</div>
                    {contact.phone && <div><span className="font-medium">Phone:</span> {contact.phone}</div>}
                    <div className="mt-3 pt-3 border-t border-[var(--border-soft)]">
                      <span className="font-medium">Message:</span>
                      <p className="mt-2 text-[var(--text-main)] leading-relaxed">{contact.message}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
